import { cacheUpdateMemberChannels } from '@/graphql/cache/cache-mutations';
import { CURSOR_ASSIGN_CHANNELS_V2 } from '@/graphql/cursor.graphql';
import {
  AssignChannelsV2Query,
  AssignChannelsV2QueryVariables,
  useAssignOrUnassignChannelToMemberMutation,
  useGetAssignedChannelsQuery,
} from '@/types/generated/graphql';
import { useCursorPagination } from '@shared/hooks/use-cursor-pagination';
import { notification } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TAssignChannelForm } from '../members.types';

export const useAssignChannels = () => {
  const [channelSearchQuery, setChannelSearchQuery] = useState('');
  const [userName, setUserName] = useState('');
  const [userWorkspaceId, setUserWorkspaceId] = useState<number | null>(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);
  const [assignedChannelIds, setAssignedChannelIds] = useState<number[]>([]);

  const queryOptions = useMemo<{
    query: string | undefined;
  }>(
    () => ({
      query: channelSearchQuery || undefined,
    }),
    [channelSearchQuery]
  );

  const {
    data: availableChannels,
    loading: availableChannelsLoading,
    initialLoading: availableChannelsInitialLoading,
    hasNextPage: hasNextChannelPage,
    loadNextRef: loadNextChannelRef,
  } = useCursorPagination<AssignChannelsV2Query, AssignChannelsV2QueryVariables>({
    schema: CURSOR_ASSIGN_CHANNELS_V2,
    responseDataKey: 'assignChannelsV2',
    variables: queryOptions,
  });

  const { data: assignedChannelsData } = useGetAssignedChannelsQuery({
    variables: { userWorkspaceId: userWorkspaceId! },
    skip: !userWorkspaceId,
    fetchPolicy: 'cache-and-network',
  });
  const [openChannelModal, setOpenChannelModal] = useState(false);

  const handleOpenAssign = () => setOpenChannelModal(true);
  const handleCloseAssign = () => {
    setSelectedChannelIds(assignedChannelIds);
    setValue('checkedChannel', assignedChannelIds);
    setChannelSearchQuery('');
    setOpenChannelModal(false);
  };

  const [assignOrUnassignChannels, { loading: assignMemberLoading }] = useAssignOrUnassignChannelToMemberMutation();

  const assignChannel = useForm<TAssignChannelForm>();
  const { handleSubmit, control: assignChannelControl, setValue } = assignChannel;

  const handleAssignSubmit = handleSubmit(async (data) => {
    if (!data?.userWorkspaceId) return;

    const myChannelIds =
      availableChannels?.data?.map((item) => item?.channel?.id).filter((id): id is number => !!id) || [];

    const checkedChannel = selectedChannelIds.filter((id) => !assignedChannelIds.includes(id));

    const uncheckedChannel = assignedChannelIds.filter(
      (id) => !selectedChannelIds.includes(id) && myChannelIds.includes(id)
    );

    await assignOrUnassignChannels({
      variables: {
        input: {
          checkedChannel,
          uncheckedChannel,
          userWorkspaceId: data.userWorkspaceId,
        },
      },
      onCompleted: (res) => {
        notification.success({ message: res?.assignOrUnassignChannelToMember?.message });
        const allAssigned = assignedChannelIds.filter((id) => !uncheckedChannel.includes(id)).concat(checkedChannel);
        setAssignedChannelIds(allAssigned);
        setSelectedChannelIds(allAssigned);
        setValue('checkedChannel', allAssigned);
        cacheUpdateMemberChannels(data.userWorkspaceId, allAssigned.length, allAssigned[0]);
        setChannelSearchQuery('');
        setOpenChannelModal(false);
      },

      onError: (error) => {
        notification.error({ message: error.message });
      },
    });
  });

  const handleRequestAssignChannel = (id: number) => {
    setUserWorkspaceId(id);
    setValue('userWorkspaceId', id);
    setOpenChannelModal(true);
  };

  const handleCloseAssignModal = handleCloseAssign;

  useEffect(() => {
    if (!assignedChannelsData?.getAssignedChannels?.data) return;

    const assignedIds = assignedChannelsData?.getAssignedChannels?.data
      ?.map((item) => item?.channel?.id)
      .filter((id): id is number => !!id);

    setAssignedChannelIds(assignedIds);
    setSelectedChannelIds(assignedIds);

    setValue('checkedChannel', assignedIds);
  }, [assignedChannelsData, setValue]);

  const hasChanges =
    selectedChannelIds.length !== assignedChannelIds.length ||
    selectedChannelIds.some((id) => !assignedChannelIds.includes(id));

  const handleCheckboxGroupChange = (checkedValues: number[]) => {
    setSelectedChannelIds(checkedValues);
    setValue('checkedChannel', checkedValues);
  };

  return {
    availableChannels,
    availableChannelsLoading,
    availableChannelsInitialLoading,
    hasNextChannelPage,
    loadNextChannelRef,
    openChannelModal,
    handleOpenAssign,
    handleCloseAssign,
    handleCloseAssignModal,
    assignChannelControl,
    handleRequestAssignChannel,
    handleAssignSubmit,
    setValue,
    assignMemberLoading,
    channelSearchQuery,
    setChannelSearchQuery,
    handleCheckboxGroupChange,
    selectedChannelIds,
    assignedChannelIds,
    userName,
    setUserName,
    assignedChannelsData,
    hasChanges,
  };
};
